import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

function requireDb() {
  if (!db) {
    throw new Error('Firestore is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

function categoriesRef(uid) {
  return collection(db, 'privacyCategories', uid, 'categories');
}

function groupsRef(uid) {
  return collection(db, 'privacyGroups', uid, 'groups');
}

function groupRef(uid, groupId) {
  return doc(db, 'privacyGroups', uid, 'groups', groupId);
}

function normalizeMembers(memberIds = []) {
  return [...new Set(memberIds.map((memberId) => String(memberId || '').trim()).filter(Boolean))].slice(0, 100);
}

export async function listPrivacyCategories(uid) {
  requireDb();
  const snapshot = await getDocs(categoriesRef(uid));
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export async function createPrivacyCategory(uid, category) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(categoriesRef(uid), {
    ownerId: uid,
    name: category.name.trim(),
    color: category.color || '#8a5a34',
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function deletePrivacyCategory(uid, categoryId) {
  requireDb();
  await deleteDoc(doc(db, 'privacyCategories', uid, 'categories', categoryId));
}

export async function listPrivacyGroups(uid) {
  requireDb();
  const snapshot = await getDocs(groupsRef(uid));
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export async function ensureDefaultPrivacyGroups(uid) {
  requireDb();
  const familyRef = groupRef(uid, 'family');
  const snapshot = await getDoc(familyRef);
  if (!snapshot.exists()) {
    const now = serverTimestamp();
    await setDoc(familyRef, {
      ownerId: uid,
      name: 'Family',
      color: '#8a5a34',
      memberIds: [],
      createdAt: now,
      updatedAt: now,
    });
  }
}

export async function createPrivacyGroup(uid, group) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(groupsRef(uid), {
    ownerId: uid,
    name: group.name.trim(),
    color: group.color || '#8a5a34',
    memberIds: normalizeMembers(group.memberIds),
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updatePrivacyGroup(uid, groupId, group) {
  requireDb();
  await updateDoc(groupRef(uid, groupId), {
    name: group.name.trim(),
    color: group.color || '#8a5a34',
    memberIds: normalizeMembers(group.memberIds),
    updatedAt: serverTimestamp(),
  });
}

export async function deletePrivacyGroup(uid, groupId) {
  requireDb();
  await deleteDoc(groupRef(uid, groupId));
}

export async function listReadablePrivacyGroupIds(ownerId, viewerUid) {
  requireDb();
  if (!viewerUid || viewerUid === ownerId) {
    return [];
  }
  const snapshot = await getDocs(query(groupsRef(ownerId), where('memberIds', 'array-contains', viewerUid)));
  return snapshot.docs.map((entry) => entry.id).slice(0, 10);
}
